import { useFormatters } from '@/hooks/useFormatters';
import { useGlobalModal } from '@/hooks/useGlobalModal';
import {
  auChipSx,
  AU_BACKDROP_SX,
  AU_DIALOG_PAPER_SX,
  AU_PAGINATION_SX,
  AU_RADIUS_CONTROL,
  AU_SCROLLBAR_SX,
} from '@/theme/au-compat';
import ApolloErrorBoundary from '@components/ApolloErrorBoundary';
import DeviceNameSpan from '@components/deviceNameSpan';
import GlobalModal from '@components/GlobalModal';
import ModalContent from '@components/modal/ModalContent';
import ModalTitle from '@components/modal/ModalTitle';
import TimeElapsed from '@components/TimeElapsed';
import TruncatedTypography from '@components/TruncatedTypography';
import { Access, AuthorizationWrapper } from '@features/authorization';
import { useGateMovementsHistory } from '@features/gateCommon/hooks/useGateMovementsHistory';
import {
  describeMovementStatus,
  describeQueue,
} from '@features/gateCommon/utils/gateMovementState';
import StandardListPagination from '@features/standardDesign/components/listView/StandardListPagination';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, Skeleton, Typography } from '@mui/material';
import { type CSSProperties, type FC, type ReactNode, Suspense, useState } from 'react';
import { MovementPosition } from '../movementsTable/MovementPosition';
import { Table } from '../movementsTable/Table';

const DEFAULT_ROWS_PER_PAGE = 20;

/**
 * El estado de una fila: el chip del estado base (`describeMovementStatus`) y, debajo, una sola
 * línea con el hecho de la cola o el título del cierre, truncada con tooltip para que todas las
 * filas midan lo mismo (`ROW_HEIGHT_PX`). El código crudo va fuera del texto truncado —la elipsis
 * no lo esconde— y el tooltip lo repite junto al motivo completo.
 */
const RowStateChip: FC<{
  movement: Parameters<typeof describeMovementStatus>[0];
  now: number;
}> = ({ movement, now }) => {
  const { i18n } = useLingui();
  const { formatDate } = useFormatters();
  const { label, tone, close } = describeMovementStatus(movement, i18n, now);
  // Debajo del chip, una sola cosa y sin tono: el hecho de la cola o el motivo del cierre.
  const note = describeQueue(movement, { i18n, now, formatDate })?.fact ?? close?.title;
  return (
    <Box sx={{ display: 'grid', justifyItems: 'center', gap: '2px', maxWidth: '100%' }}>
      <Box sx={{ ...auChipSx(tone), maxWidth: '100%' }} title={label}>
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {label}
        </Box>
      </Box>
      {note && (
        <Box sx={ROW_NOTE_LINE_SX}>
          <TruncatedTypography
            sx={ROW_NOTE_SX}
            truncatedTitle={
              close && (
                <>
                  {note}
                  <CloseCode code={close.code} />
                </>
              )
            }
          >
            {note}
          </TruncatedTypography>
          {close && <CloseCode code={close.code} />}
        </Box>
      )}
    </Box>
  );
};

/** El gris de «hace X» bajo la fecha: el texto bajo el chip no compite con él. */
const ROW_NOTE_SX = {
  fontSize: 10.5,
  lineHeight: 1.35,
  color: 'text.secondary',
  minWidth: 0,
} as const;

/**
 * La línea bajo el chip: el motivo encoge y se trunca, el código no. Alto fijo: el monoespaciado del
 * código tiene otra métrica y, sin esto, las filas con código medían 2px más.
 */
const ROW_NOTE_LINE_SX = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'baseline',
  maxWidth: '100%',
  minWidth: 0,
  height: '14px',
} as const;

/**
 * Código crudo del cierre a continuación del motivo, solo para quien ve los campos técnicos
 * (`.oc-movements-code`). De módulo: `AuthorizationWrapper` suspende y no debe remontarse.
 */
const CloseCode: FC<{ code: string }> = ({ code }) => (
  <AuthorizationWrapper access={Access.ViewGateMovementAdvancedFields} fallback={null}>
    <Box component="code" sx={CLOSE_CODE_SX}>
      {code}
    </Box>
  </AuthorizationWrapper>
);

// `&&&`: `AU_DIALOG_PAPER_SX` fuerza `font-family: inherit` con `.<paper>.<paper> *`, dos clases
// que le ganan a la de Emotion; con tres el monoespaciado se queda.
const CLOSE_CODE_SX = {
  ml: '6px',
  flexShrink: 0,
  fontSize: 10.5,
  color: 'var(--au-primary-text)',
  '&&&': { fontFamily: 'var(--au-mono)' },
} as const;

/**
 * El reparto de las siete columnas, portado de `.oc-movements-table[data-detailed]
 * .oc-movements-row` (`operar-compuerta.css:594-599`).
 *
 * **Todas en `fr` con su mínimo, ninguna en `auto`.** Con `auto` cada columna mide su propio
 * contenido, así que el ancho de «Solicitada» dependería de si algún número tiene tres cifras y la
 * fila entera se re-repartiría al cambiar un dato. El ID lleva el mínimo más chico —son dígitos de
 * ancho fijo y no tiene nada que elidir—, y lo que le sobra se lo queda la columna de usuario, que
 * sí elide.
 */
const GRID_TEMPLATE_COLUMNS = [
  'minmax(64px, 0.5fr)',
  // Tope fijo: un nombre largo no se lleva el ancho que necesita el estado; se trunca con tooltip.
  'minmax(140px, 220px)',
  'minmax(110px, 1fr)',
  'minmax(80px, 0.7fr)',
  'minmax(130px, 1.1fr)',
  'minmax(130px, 1.1fr)',
  'minmax(150px, 1.6fr)',
].join(' ');

/** El aire entre las siete columnas, de la misma regla del espécimen que el reparto de arriba. */
const GRID_GAP = '14px';

/** El alto de una fila y el del encabezado, medidos en el espécimen (`operar-compuerta.css:666`). */
const ROW_HEIGHT_PX = 49;
const HEAD_HEIGHT_PX = 30;

/** El hueco de lo que **todavía no pasó** — distinto de `??`, que es lo que nunca se reportó. */
const PendingCell: FC = () => (
  <Typography
    sx={{
      fontSize: 11.5,
      textAlign: 'center',
      whiteSpace: 'nowrap',
      color: 'text.secondary',
      fontStyle: 'italic',
    }}
  >
    <Trans>Pendiente</Trans>
  </Typography>
);

const HeaderCell: FC<{ children: ReactNode; align?: 'left' | 'center' }> = ({
  children,
  align = 'center',
}) => (
  /* Un `Box` y no un `span`: `text-align` sobre una caja inline alinea el contenido *dentro* de
     ella, y la caja de un `span` mide lo que mide su texto — no se centra en la celda. Es el modo
     de fallar que este reparto tiene: la alineación se declara y no pasa nada, sin ruido. */
  <Box sx={{ textAlign: align }}>{children}</Box>
);

const ValueCell: FC<{ children: ReactNode }> = ({ children }) => (
  <Typography
    sx={{ fontSize: 11.5, textAlign: 'center', whiteSpace: 'nowrap', color: 'text.primary' }}
  >
    {children}
  </Typography>
);

interface Props {
  gateId: string;
}

/**
 * El listado paginado, sin la cáscara del diálogo.
 *
 * Exportado para el test: la cáscara arrastra el estado global de modales y el gate de
 * autorización, que no son lo que esta tabla promete —siete columnas, los dos huecos distintos y
 * un alto que no salta—, y montarla para verificar eso pedía media app en pie.
 */
export const PaginatedHistoryContent: FC<Props> = ({ gateId }) => {
  const { formatMetricValue, formatDateTime } = useFormatters();
  const { i18n } = useLingui();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const { items, pageInfo, unit, precision } = useGateMovementsHistory(gateId, page, rowsPerPage);

  const formatPosition = (position: number) => formatMetricValue(position, { unit, precision });

  /**
   * El alto lo reserva **la página más llena que puede haber**, no las filas que llegaron: sin
   * esto la última página —tres filas de veinte— encoge el diálogo y el paginador salta a mitad de
   * pantalla justo cuando hay que volver a tocarlo.
   *
   * Y es el mínimo contra el total, no el tamaño de página a secas: con veinte por página sobre
   * seis movimientos, reservar por el tamaño abre catorce filas de aire.
   */
  const reservedRows = Math.min(rowsPerPage, pageInfo.totalCount);

  const headers = [
    <HeaderCell key="id">
      <Trans>ID</Trans>
    </HeaderCell>,
    <HeaderCell key="user" align="left">
      <Trans>Solicitado por</Trans>
    </HeaderCell>,
    /* La columna lleva dos números, así que el encabezado dice cuál es cuál. Es una segunda línea
       atenuada y no una fila de sub-encabezados: «Inicial → Final» se lee de corrido. */
    <Box
      key="position"
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}
    >
      <Box component="span">
        <Trans>Posición</Trans>
      </Box>
      <Box component="span" sx={{ fontSize: 10, fontWeight: 600, color: 'text.secondary' }}>
        <Trans>Inicial → Final</Trans>
      </Box>
    </Box>,
    <HeaderCell key="requested">
      <Trans>Solicitada</Trans>
    </HeaderCell>,
    <HeaderCell key="requestedAt">
      <Trans>Fecha solicitud</Trans>
    </HeaderCell>,
    <HeaderCell key="finishedAt">
      <Trans>Fecha finalización</Trans>
    </HeaderCell>,
    /* «Estado movimiento»: la columna es el estado de cada movimiento; «Estado compuerta» es el
       del box del widget de operación, y compartir rótulo los confundía. */
    <HeaderCell key="state">
      <Trans>Estado movimiento</Trans>
    </HeaderCell>,
  ];

  // El historial no lleva reloj: el vencimiento se evalúa al pintar la página.
  const now = Date.now();
  const rows = items.map((mov) => ({
    columns: [
      /* `tabular-nums` porque es una columna de números de igual largo leídos en vertical: sin
         ancho fijo de dígito los de una fila no caen bajo los de la siguiente. Atenuado porque
         identifica la fila, no la califica — el dato que se lee es el resto. */
      <Typography
        key="id"
        sx={{
          fontSize: 11.5,
          textAlign: 'center',
          whiteSpace: 'nowrap',
          color: 'text.secondary',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {mov.id}
      </Typography>,
      <Box key="user" sx={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0 }}>
        {mov.requestedBy.name ? (
          <TruncatedTypography sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1.2 }}>
            {mov.requestedBy.name}
          </TruncatedTypography>
        ) : (
          <TruncatedTypography
            sx={{ fontSize: 12, fontWeight: 700, fontStyle: 'italic', lineHeight: 1.2 }}
          >
            {/* Sin `id` no hubo usuario de Amaru detrás: es una operación manual —la que abre el
                operario en terreno—, no un usuario que no se pudo resolver (AMARU-862). */}
            {mov.requestedBy.id == null ? (
              <Trans>Operación manual</Trans>
            ) : (
              <Trans>Usuario desconocido</Trans>
            )}
          </TruncatedTypography>
        )}
        <TruncatedTypography sx={{ fontSize: 10.5, lineHeight: 1.2, color: 'text.secondary' }}>
          <TimeElapsed timestamp={mov.requestedAt} />
        </TruncatedTypography>
      </Box>,
      /* `!= null` sobre la **tupla** y no sobre el valor: una posición de 0 cm —compuerta
         cerrada— es un dato válido y no un hueco. Ver `MovementPosition`. */
      <MovementPosition
        key="position"
        detailed
        from={mov.startPosition != null ? mov.startPosition[1] : null}
        to={mov.finishPosition != null ? mov.finishPosition[1] : null}
        requested={mov.requestedPosition != null ? mov.requestedPosition[1] : null}
        format={formatPosition}
        closed={mov.finishedAt != null}
      />,
      /* Una operación manual no tiene destino solicitado; la fecha de solicitud sí existe siempre y
         viene por `requestedAt`, no por el punto solicitado (AMARU-862). */
      mov.requestedPosition != null ? (
        <ValueCell key="requested">{formatPosition(mov.requestedPosition[1])}</ValueCell>
      ) : (
        <ValueCell key="requested">
          <Box component="span" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            <Trans>Sin destino</Trans>
          </Box>
        </ValueCell>
      ),
      <ValueCell key="requestedAt">{formatDateTime(mov.requestedAt)}</ValueCell>,
      // La fecha sale de `finishedAt` y no de la tupla de posición final: una solicitud cancelada o
      // vencida cierra sin posición, y leerla de la tupla la dejaba «Pendiente» ya cerrada.
      mov.finishedAt != null ? (
        <ValueCell key="finishedAt">{formatDateTime(mov.finishedAt)}</ValueCell>
      ) : (
        <PendingCell key="finishedAt" />
      ),
      <Box key="state" sx={{ display: 'grid', justifyItems: 'center' }}>
        {/* El chip de la capa en su `subtle`: la tabla ya tiene su propia línea entre filas, y un
            borde acá sumaba una segunda separación sobre la misma junta. El tono sale de
            `stateTone` y no de `getStateColor`, que devuelve rellenos sólidos para marcas
            gráficas (D-003 del PR 1). */}
        <RowStateChip movement={mov} now={now} />
      </Box>,
    ],
  }));

  return (
    /* Las tres zonas: el encabezado se pega arriba, el área de scroll sostiene **solo las filas**,
       y el paginador queda afuera. Sin esa separación una página de veinte filas manda el diálogo
       entero a scroll y el paginador —que es lo que hay que volver a tocar— se va con él.
       `minWidth: 0` acá y en el área de scroll: un hijo de flex no baja del ancho de su contenido,
       y sin eso la grilla empuja al diálogo y el scroll horizontal aparece en el diálogo entero. */
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', minHeight: 0, minWidth: 0 }}>
      <Box
        // La custom property viaja desde acá para que la aritmética viva junto al alto de fila del
        // que depende, igual que en la hoja del espécimen.
        style={{ '--oc-movements-rows': reservedRows } as CSSProperties}
        sx={{
          overflow: 'auto',
          minWidth: 0,
          // La reserva va en el área y no en la tabla, con el mismo tope que su `maxHeight`: en la
          // tabla, una última página de pocas filas medía igual que una llena y el área dejaba
          // scrollear ese aire.
          minHeight: `min(calc(var(--oc-movements-rows) * ${ROW_HEIGHT_PX}px + ${HEAD_HEIGHT_PX}px), 52vh)`,
          maxHeight: '52vh',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: AU_RADIUS_CONTROL,
          ...AU_SCROLLBAR_SX,
        }}
      >
        <Table
          label={i18n._(msg`Movimientos de la compuerta`)}
          headers={headers}
          rows={rows}
          gridTemplateColumns={GRID_TEMPLATE_COLUMNS}
          gap={GRID_GAP}
          stickyHeader
          sx={{
            // Todas las filas al mismo alto: la fila centra su contenido, así que un chip sin
            // motivo queda al medio en vez de arriba de una línea vacía.
            '& > [role="row"] ~ [role="row"]': { minHeight: `${ROW_HEIGHT_PX}px` },
            // Con scroll horizontal la tabla mide lo que suman los mínimos de las columnas y no el
            // ancho del área: si no, el fondo del encabezado y los bordes de fila se cortan donde
            // termina el área y el contenido sigue de largo.
            minWidth: 'min-content',
          }}
        />
      </Box>
      <StandardListPagination
        totalItems={pageInfo.totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        // Sin `setPage(1)` a mano: `StandardListPagination` ya vuelve a la primera al cambiar el
        // tamaño, y duplicarlo acá era dos fuentes para el mismo reset.
        onRowsPerPageChange={setRowsPerPage}
        slotProps={{
          /* Pisan los defaults responsive del componente, que en ≥md pone `large` y enciende los
             saltos a primera y última. Con el rango completo a la vista los extremos ya están
             dibujados como números, así que ese par de flechas dobles repite dos destinos que ya
             se pueden tocar. */
          pagination: {
            size: 'small',
            showFirstButton: false,
            showLastButton: false,
            siblingCount: 1,
            boundaryCount: 1,
            sx: AU_PAGINATION_SX,
          },
        }}
        sx={{ flexShrink: 0 }}
      />
    </Box>
  );
};

/**
 * El chrome del diálogo entra por `dialogProps`, siguiendo `MoveGateModal.auDialogProps`.
 *
 * `maxWidth: 'xl'` con `fullWidth`: las columnas van en `fr` y se reparten el ancho, así que la tabla
 * no flota en un diálogo ancho; el espacio extra va al estado y a las fechas, y «Solicitado por»
 * tiene tope (`GRID_TEMPLATE_COLUMNS`). Más angosto que los mínimos, el scroll horizontal queda en
 * el área de la tabla.
 *
 * **Sin barra de acciones.** El diálogo es de lectura: el único «Cerrar» que necesita es la cruz de
 * `ModalTitle`, y un pie que la repita gastaría una franja del alto de la tabla.
 */
const auDialogProps = {
  maxWidth: 'xl' as const,
  fullWidth: true,
  PaperProps: { sx: AU_DIALOG_PAPER_SX },
  slotProps: { backdrop: { sx: AU_BACKDROP_SX } },
};

export const GateMovementsHistoryModal: FC = () => {
  const { globalModalProps, state, onClose } = useGlobalModal('gateMovementsHistoryModal');

  return (
    <GlobalModal
      {...globalModalProps}
      dialogProps={auDialogProps}
      authorization={
        state.open
          ? {
              access: Access.ViewGateMovementsHistory,
              deviceId: state.gateId,
            }
          : undefined
      }
    >
      <ModalTitle
        onClose={onClose}
        title={<Trans>Ver historial de movimientos</Trans>}
        subtitle={
          state.open ? (
            <DeviceNameSpan
              deviceId={state.gateId}
              variant="subtitle1"
              color="text.secondary"
              lineHeight={1}
            />
          ) : null
        }
      />
      {/* Sin alto fijo: el alto lo reserva la tabla por su tamaño de página y el `maxHeight` del
          área de scroll lo acota contra el viewport. Los 600px de antes abrían aire con pocos
          movimientos y no evitaban nada con muchos.

          `bottomDivider={false}` porque los dos filos internos del diálogo son los `dividers` de
          este `DialogContent`, y el de abajo es el que separa del pie. Retirado el pie, ese filo
          queda como una línea suelta apoyada contra la esquina redondeada del Paper. */}
      <ModalContent bottomDivider={false} sx={{ display: 'flex', flexDirection: 'column' }}>
        {state.open && state.gateId && (
          <ApolloErrorBoundary variant="section-alert">
            <Suspense
              fallback={
                <Skeleton
                  variant="rounded"
                  sx={{
                    height: DEFAULT_ROWS_PER_PAGE * ROW_HEIGHT_PX + HEAD_HEIGHT_PX,
                    // El mismo tope que el área de scroll, para que el diálogo no cambie de alto
                    // entre el esqueleto y la tabla.
                    maxHeight: '52vh',
                  }}
                />
              }
            >
              <PaginatedHistoryContent gateId={state.gateId} />
            </Suspense>
          </ApolloErrorBoundary>
        )}
      </ModalContent>
    </GlobalModal>
  );
};
